import React from 'react';
import { Box, Button, Divider, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import UserAvatar from '../avatar/UserAvatar';
import { AdminTable } from '../adminTable';
import configuration from '../../config/Config';
import { formatMoney } from '../../utils/configUtils';
import StatusChip from '../statusChip/StatusChip';
import ReyLeaf from '../assests/reyleafLogo.svg';

/* ─── Internal helpers ───────────────────────────────────────────── */
const StarRating = ({ rating = 0 }) => {
  const full = Math.floor(rating);
  const half = rating - full > 0;
  return (
    <Box sx={{ display: 'flex' }}>
      {Array.from({ length: full }, (_, i) => (
        <StarIcon key={i} sx={{ color: '#FFD700', fontSize: 18 }} />
      ))}
      {half && <StarHalfIcon sx={{ color: '#FFD700', fontSize: 18 }} />}
      {Array.from({ length: 5 - full - (half ? 1 : 0) }, (_, i) => (
        <StarOutlineIcon key={`e${i}`} sx={{ color: '#FFD700', fontSize: 18 }} />
      ))}
    </Box>
  );
};

const Field = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo', mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', fontFamily: 'Heebo' }}>
      {children || '—'}
    </Box>
  </Box>
);

/* ─── Main component ─────────────────────────────────────────────── */
const ProductDetailModal = ({ open, onClose, product, reviews, onAccept, onReject }) => (
  <Modal open={open} onClose={onClose}>
    <Box sx={{
      position: 'absolute', top: '50%', left: '50%',
      transform: 'translate(-50%, -50%)',
      width: { xs: '95vw', sm: '88vw', md: '700px' },
      maxHeight: '88vh',
      bgcolor: '#fff',
      borderRadius: '24px',
      boxShadow: '0 24px 64px rgba(0,0,0,0.13)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', outline: 'none',
    }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Heebo' }}>
          Product Details
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#6b7280', bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#ebebeb' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Scrollable body ── */}
      <Box sx={{ overflowY: 'auto', px: 3, py: 2.5, flex: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
        {product ? (
          <>
            {/* Hero image */}
            <Box sx={{ borderRadius: '16px', overflow: 'hidden', mb: 2, bgcolor: '#f8f9fa', maxHeight: 260 }}>
              <img
                src={product?.photos?.length > 0 ? `${configuration.cdnFront}/${product.photos[0].url}` : ReyLeaf}
                alt="product"
                style={{ width: '100%', height: '260px', objectFit: 'cover', display: 'block' }}
              />
            </Box>

            {/* Thumbnail strip */}
            {product?.photos?.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {product.photos.map((img) => (
                  <Box
                    key={img?._id}
                    sx={{ width: 72, height: 72, borderRadius: '12px', overflow: 'hidden', flexShrink: 0, bgcolor: '#f8f9fa' }}
                  >
                    <img
                      src={img?.url ? `${configuration.cdnFront}/${img.url}` : ReyLeaf}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {/* Meta fields */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2.5, mb: 3 }}>
              <Field label="Product Name">{product?.title}</Field>
              <Field label="Category">{product?.categoryId?.name}</Field>
              <Field label="Price">{formatMoney(product?.price)}</Field>
              <Field label="Discounted Price">{formatMoney(product?.discountedProce)}</Field>
              {product?.status && (
                <Field label="Status"><StatusChip value={product.status} /></Field>
              )}
            </Box>

            {/* Description */}
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', fontFamily: 'Heebo', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Description
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#362B24', fontFamily: 'Heebo', lineHeight: 1.7, mb: 3, opacity: 0.75 }}>
              {product?.description || '—'}
            </Typography>

            {/* Reviews */}
            {reviews?.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', fontFamily: 'Heebo', mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Reviews & Ratings
                </Typography>
                {reviews.map((item, idx) => (
                  <Box key={item._id || idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <UserAvatar
                          src={item?.user?.picture ? `${configuration.cdnFront}/${item.user.picture}` : undefined}
                          name={item?.user?.fullname}
                          size={40}
                        />
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a', fontFamily: 'Heebo' }}>
                            {item?.user?.fullname || '—'}
                          </Typography>
                          {item?.createdAt && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                              <AccessTimeIcon sx={{ fontSize: 12, color: '#065F46' }} />
                              <Typography sx={{ fontSize: 11, color: '#065F46', fontFamily: 'Heebo' }}>
                                {new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StarRating rating={item?.rating || 0} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', fontFamily: 'Heebo' }}>
                          {item?.rating || 0}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 13, color: '#362B24', fontFamily: 'Heebo', lineHeight: 1.6, opacity: 0.65, pl: 0.5 }}>
                      {item?.review || '—'}
                    </Typography>
                    {idx < reviews.length - 1 && <Divider sx={{ my: 1.5 }} />}
                  </Box>
                ))}
              </>
            )}

            {/* Accept / Reject actions (NewAddedProduct only) */}
            {(onAccept || onReject) && (
              <>
                <Divider sx={{ mb: 2, mt: 3 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {onAccept && (
                    <Button
                      variant="text"
                      onClick={onAccept}
                      startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                      sx={{
                        flex: 1,
                        borderRadius: '10px',
                        fontFamily: 'Heebo',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#065F46',
                        '&:hover': { bgcolor: '#ECFDF5' },
                      }}
                    >
                      Accept
                    </Button>
                  )}
                  {onReject && (
                    <Button
                      variant="text"
                      onClick={onReject}
                      startIcon={<CancelOutlinedIcon fontSize="small" />}
                      sx={{
                        flex: 1,
                        borderRadius: '10px',
                        fontFamily: 'Heebo',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#EA001B',
                        '&:hover': { bgcolor: '#FEF2F2' },
                      }}
                    >
                      Reject
                    </Button>
                  )}
                </Box>
              </>
            )}
          </>
        ) : (
          <Typography sx={{ color: '#9ca3af', fontFamily: 'Heebo', textAlign: 'center', py: 6 }}>
            Loading product…
          </Typography>
        )}
      </Box>
    </Box>
  </Modal>
);

export default ProductDetailModal;
